<template>
  <div 
    class="print-header" 
    v-show="showTitle || showDescription"
    style="position: relative; z-index: 1;"
  >
    <div v-if="showTitle" class="title-container">
      <h1 class="list-title">{{ title }}</h1>
      <v-btn
        v-if="showListSelectorButton"
        icon="mdi-format-list-bulleted-square"
        size="small"
        variant="text"
        class="edit-button screen-only"
        title="Switch List"
        @click="$emit('switch-list')"
      ></v-btn>
      <v-btn
        v-if="showEditButton"
        icon="mdi-pencil"
        size="small"
        variant="text"
        class="edit-button screen-only"
        @click="$emit('edit')"
      ></v-btn>
    </div>
    <p v-if="showDescription && description" class="list-description">
      {{ description }}
    </p>
    <div v-if="showMetadata" class="print-meta">
      <span>{{ totalItems }} items</span>
      <span v-if="checkedItems > 0" class="separator">•</span>
      <span v-if="checkedItems > 0">{{ checkedItems }} checked</span>
      <span v-if="duration && duration > 1" class="separator">•</span>
      <span v-if="duration && duration > 1">{{ formatDuration(duration) }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrintHeader',
  emits: ['edit', 'switch-list'],
  methods: {
    formatDuration(days) {
      if (!days || days === 1) return ''
      
      if (days === 7) return '1 week'
      if (days === 14) return '2 weeks'
      if (days === 21) return '3 weeks'
      if (days === 28) return '4 weeks'
      
      // For weeks + days
      if (days > 7 && days % 7 === 0) {
        const weeks = Math.floor(days / 7)
        return `${weeks} weeks`
      }
      
      // For mixed weeks and days
      if (days > 7) {
        const weeks = Math.floor(days / 7)
        const remainingDays = days % 7
        if (remainingDays === 0) {
          return `${weeks} week${weeks !== 1 ? 's' : ''}`
        }
        return `${weeks} week${weeks !== 1 ? 's' : ''} and ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`
      }
      
      // Just days
      return `${days} days`
    }
  },
  props: {
    showTitle: {
      type: Boolean,
      default: true
    },
    showDescription: {
      type: Boolean,
      default: true
    },
    showMetadata: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'List'
    },
    description: {
      type: String,
      default: ''
    },
    duration: {
      type: Number,
      default: 1
    },
    totalItems: {
      type: Number,
      default: 0
    },
    checkedItems: {
      type: Number,
      default: 0
    },
    formattedDate: {
      type: String,
      default: ''
    },
    showEditButton: {
      type: Boolean,
      default: false
    },
    showListSelectorButton: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>
/* Print Header */
.print-header {
  text-align: center;
  margin-bottom: 1rem;
  border-bottom: 2px solid #333;
  padding-bottom: 0.5rem;
}

.list-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
  color: #333;
}

.list-description {
  font-size: 1rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.print-meta {
  font-size: 0.9rem;
  color: #666;
}

.separator {
  margin: 0 0.5rem;
}

.title-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.edit-button {
  opacity: 0.7;
}

.edit-button:hover {
  opacity: 1;
}

.screen-only {
  display: initial;
}

/* Default compact styles */
.print-header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.375rem;
}

.list-title {
  font-size: 1.4rem;
  margin-bottom: 0.15rem;
}

.list-description {
  font-size: 0.8rem;
  margin-bottom: 0.3rem;
}

.print-meta {
  font-size: 0.8rem;
}

/* Hide edit button when printing */
@media print {
  .screen-only {
    display: none !important;
  }
  .print-header {
    position: relative !important;
    z-index: 1 !important;
    background: rgba(255, 255, 255, 0.8) !important;
  }
  
  /* Default compact size for print */
  .list-title {
    font-size: 12pt !important;
    margin-bottom: 0.1rem !important;
  }
  
  .list-description {
    font-size: 9pt !important;
    margin-bottom: 0.2rem !important;
  }
  
  .print-meta {
    font-size: 9pt !important;
  }
  
  /* Prevent page breaks between header and content - Chrome specific fixes */
  .print-header {
    page-break-after: avoid !important;
    break-after: avoid !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    /* Chrome-specific: Reduce margins and force inline-block */
    margin-bottom: 0.3rem !important;
    padding-bottom: 0.2rem !important;
    display: block !important;
    /* Chrome fix: Prevent orphaning */
    orphans: 3;
    widows: 3;
  }
  
  /* Adjust header sizing for print to prevent page breaks */
  .print-header {
    margin-bottom: 0.4rem !important;
    padding-bottom: 0.2rem !important;
  }
  
  /* Comprehensive 4-column print fixes */
  .columns-4 .print-header {
    page-break-after: avoid !important;
    break-after: avoid !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    margin-bottom: 0.2rem !important;
    padding-bottom: 0.1rem !important;
  }
  
  /* 4-column specific sizing */
  .columns-4 .list-title {
    font-size: 10pt !important;
    margin-bottom: 0.05rem !important;
  }
  
  .columns-4 .list-description {
    font-size: 8pt !important;
    margin-bottom: 0.1rem !important;
  }
  
  .columns-4 .print-meta {
    font-size: 7pt !important;
  }
}
</style>